import { useState } from 'react'
import { endMeeting, rateMeeting, type Meeting, type MeetingRecap } from '../lib/meetingApi'
import CascadeOut from './CascadeOut'

/** What the meeting did so far — live while open, frozen once ended. */
export function Recap({ recap }: { recap: MeetingRecap }) {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  // 0025. Absent from recaps frozen before then, and only shown when non-zero
  // so a meeting with no cascading messages reads exactly as before.
  const cascadesIn = recap.cascades_in ?? []
  const cascadesOut = recap.cascades_out ?? []
  const cascadesShared = recap.cascades_shared ?? 0
  const cascadesCreated = recap.cascades_created ?? 0
  return (
    <ul className="mini recap">
      {cascadesShared > 0 && (
        <li className="mini__row">
          <span className="mini__title">
            {plural(cascadesShared, 'cascading message', 'cascading messages')} read out
          </span>
          <span className="mini__note">
            {cascadesIn.map((c) => `${c.body} (from ${c.from})`).join(' · ')}
          </span>
        </li>
      )}
      <li className="mini__row">
        <span className="mini__title">{plural(recap.headlines_shared, 'headline', 'headlines')} shared</span>
        {recap.headlines.length > 0 && (
          <span className="mini__note">{recap.headlines.map((h) => h.body).join(' · ')}</span>
        )}
      </li>
      <li className="mini__row">
        <span className="mini__title">{plural(recap.issues_solved, 'issue', 'issues')} solved</span>
        {recap.issues_solved_list.length > 0 && (
          <span className="mini__note">{recap.issues_solved_list.map((i) => i.title).join(' · ')}</span>
        )}
      </li>
      <li className="mini__row">
        <span className="mini__title">
          {plural(recap.issues_reported, 'issue', 'issues')} reported
          {recap.issues_archived > 0 ? ` · ${recap.issues_archived} archived` : ''}
        </span>
      </li>
      <li className="mini__row">
        <span className="mini__title">{plural(recap.tasks_created, 'to-do', 'to-dos')} created</span>
        {recap.tasks.length > 0 && (
          <span className="mini__note">{recap.tasks.map((t) => t.title).join(' · ')}</span>
        )}
      </li>
      {cascadesCreated > 0 && (
        <li className="mini__row">
          <span className="mini__title">
            {plural(cascadesCreated, 'message', 'messages')} sent to other teams
          </span>
          <span className="mini__note">
            {cascadesOut.map((c) => `${c.body} → ${c.teams ?? 'no team'}`).join(' · ')}
          </span>
        </li>
      )}
      <li className="mini__row">
        <span className="mini__title">
          {recap.rating_count === 0
            ? 'Not rated yet'
            : `Rated ${recap.rating_avg} · ${plural(recap.rating_count, 'rating', 'ratings')}`}
        </span>
      </li>
    </ul>
  )
}

/** 1–10, two rows of five, your pick highlighted. Saves on tap. */
export function RatingGrid({
  value,
  busy,
  onPick,
}: {
  value: number | null
  busy: boolean
  onPick: (rating: number) => void
}) {
  return (
    <div className="rating" role="group" aria-label="Rate this meeting, 1 to 10">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          className={'seg__btn rating__btn' + (value === n ? ' seg__btn--on' : '')}
          aria-pressed={value === n}
          disabled={busy}
          onClick={() => onPick(n)}
        >
          {n}
        </button>
      ))}
    </div>
  )
}

interface Props {
  meeting: Meeting
  token: string
  isAdmin: boolean
  /** Called after any write so Meeting polls straight away. */
  onChanged: () => void
}

/**
 * Conclude: the recap, cascading messages, the rating, and — for the
 * presenter — End meeting.
 *
 * Cascading messages are the EOS Conclude question "does anything need to be
 * told to another team?" (0025). Everyone sees what is being sent; admins
 * write them, and the weekday 5:53pm Ninety sync posts them.
 *
 * "Archive solved issues" is ON by default because that is what Ninety does
 * at Conclude and what Scott chose; it can be unticked per meeting. Ending
 * asks once, inline, rather than with a browser dialog: it is the one action
 * in the meeting that cannot be undone.
 */
export default function MeetingConclude({ meeting, token, isAdmin, onChanged }: Props) {
  const [archiveSolved, setArchiveSolved] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const rate = async (n: number) => {
    setBusy(true)
    setProblem(null)
    try {
      await rateMeeting(token, meeting.id, n)
      onChanged()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const end = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await endMeeting(token, meeting.id, archiveSolved)
      setConfirming(false)
      onChanged()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      {problem && <p className="banner">{problem}</p>}

      <div className="group">
        <h2 className="group__label">This meeting</h2>
        <Recap recap={meeting.recap} />
      </div>

      <CascadeOut
        token={token}
        isAdmin={isAdmin}
        meetingId={meeting.id}
        layout="group"
        onChanged={onChanged}
        refreshKey={meeting.recap.cascades_created ?? 0}
      />

      <div className="group">
        <h2 className="group__label">
          Rate it
          {meeting.my_rating != null && <span className="group__count">{meeting.my_rating}</span>}
        </h2>
        <p className="muted meeting-conclude__lead">
          How was the meeting, 1 to 10? You can change it until an hour after it ends.
        </p>
        <RatingGrid value={meeting.my_rating} busy={busy} onPick={(n) => void rate(n)} />
      </div>

      {isAdmin && (
        <div className="group meeting-end">
          <h2 className="group__label">End the meeting</h2>
          <label className="check">
            <input
              type="checkbox"
              checked={archiveSolved}
              onChange={(e) => setArchiveSolved(e.target.checked)}
            />
            <span>Archive solved issues — they leave the Issues list but stay in the record</span>
          </label>

          {confirming ? (
            <div className="meeting-end__confirm">
              <p className="muted">End the meeting now? The recap is frozen as it stands.</p>
              <div className="form__actions">
                <button
                  type="button"
                  className="btn btn--quiet"
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button type="button" className="btn" disabled={busy} onClick={() => void end()}>
                  {busy ? 'Ending…' : 'Yes, end it'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              End meeting
            </button>
          )}
        </div>
      )}
    </section>
  )
}
